import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useMaterialSearchStore } from '$/components/core/Collection/MaterialSearch/store/useMaterialSearchStore';
import { type MaterialType } from '$/services/usecases/materials/mapper/materials';
import { materialsQuery } from '$/services/usecases/materials/queries';
import { useCollectionStore } from '$/stores/useCollectionStore';

export type UseMaterialsPayload = {
  type?: MaterialType;
  ids?: string[];
  limit?: number;
  hideProMaterials?: boolean;
  disabled?: boolean;
  isDemo?: boolean;
};

export const useMaterials = (opt: UseMaterialsPayload = {}) => {
  const brightnessFilter = useCollectionStore.useBrightnessFilter();
  const filterOptions = useCollectionStore.useAppliedFilterOptions();
  const query = useMaterialSearchStore.useQuery();

  const [showSkeleton, setShowSkeleton] = useState(false);

  const hasCollectionFilter =
    opt.type === 'color_tint' &&
    filterOptions.some((option) => option.option === 'collection');

  const queryRes = useQuery(
    materialsQuery(
      {
        limit: opt.limit ?? 100,
        filter: [
          ...filterOptions,
          ...(opt.ids ? [{ option: 'id', value: opt.ids }] : []),
        ],
        brightnessFilter,
        type: opt.type,
        query: query,
        order: hasCollectionFilter ? 'page asc, pagePosition asc' : undefined,
        isDemo: opt.isDemo,
      },
      { keepPrevious: true, disabled: opt.disabled },
    ),
  );

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (queryRes.isLoading) {
      timer = setTimeout(() => setShowSkeleton(true), 500);
    } else {
      setShowSkeleton(false);
    }
    return () => clearTimeout(timer);
  }, [queryRes.isLoading]);

  if (opt.disabled)
    return [
      { isLoading: false, isError: false, data: { results: [], count: 0 } },
      false,
      [],
      0,
    ] as const;

  return [
    queryRes,
    showSkeleton,
    queryRes.data?.results,
    queryRes.data?.count ?? 0,
  ] as const;
};
